import { useState } from 'react'
import { Button } from './ui/primitives'

/**
 * **Anlegen und Ändern eines Meilensteins sind dasselbe Formular**
 * (28.08.2026, [E-273](../../../../docs/ENTSCHEIDUNGEN.md#e-273)).
 *
 * Bis dahin ließ sich ein Meilenstein nur anlegen, abhaken oder wegwerfen. Genau die häufigste
 * Änderung fehlte damit: **der Termin verschiebt sich, der Meilenstein bleibt.** Wer ihn dafür
 * löschen und neu anlegen muss, verliert seine Aufgabenbezüge – die Aufgaben zeigen danach ins
 * Leere und müssen von Hand wieder zugeordnet werden.
 *
 * **Der Termin darf leer bleiben.** Ein Meilenstein steht oft fest, bevor sein Datum feststeht:
 * *„Abnahme beim Kunden"* ist auch ohne Tag eine Aussage. Ein erfundenes Datum wäre schlimmer
 * als keines, weil die Planung damit rechnet und Aufgaben in eine Woche zieht, die niemand
 * zugesagt hat.
 *
 * Ein Formular für beides, weil beides dieselben zwei Felder hat: wer einen Meilenstein anlegen
 * kann, soll ihn ändern können, ohne etwas Zweites zu lernen. Der Name ist Pflicht – ein
 * Meilenstein ohne Bedeutung ist keiner.
 */
export function MeilensteinFormular({
  label: start,
  termin: startTermin,
  laeuft,
  aktion,
  schmal = false,
  onSpeichern,
  onAbbrechen,
  onEntfernen,
}: {
  label: string
  termin: string | null
  laeuft: boolean
  /** Beschriftung des Bestätigungsknopfs – „Hinzufügen" beim Anlegen, „Speichern" beim Ändern. */
  aktion: string
  /** In der Vorhaben-Zeile ist die Spalte schmal: dort bricht das Namensfeld auf volle Breite. */
  schmal?: boolean
  onSpeichern: (werte: { label: string; termin: string | null }) => void
  onAbbrechen: () => void
  /** Nur beim Ändern: löschen steht dort, wo man den Meilenstein ohnehin gerade anfasst. */
  onEntfernen?: () => void
}) {
  const [label, setLabel] = useState(start)
  const [termin, setTermin] = useState(startTermin ?? '')

  /** Dieselbe Untergrenze wie im Router (`min(3)`) – der Knopf soll nicht in einen Fehler laufen. */
  const gueltig = label.trim().length >= 3

  return (
    <form
      className="mt-1.5 flex flex-wrap items-center gap-1.5"
      onSubmit={(e) => {
        e.preventDefault()
        if (gueltig) onSpeichern({ label: label.trim(), termin: termin || null })
      }}
    >
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="z. B. Abnahme beim Kunden"
        aria-label="Meilenstein"
        autoFocus
        className={`rounded-md border border-line bg-surface px-2 py-1 text-[12px] ${
          schmal ? 'w-full' : 'w-[240px]'
        }`}
      />
      <input
        type="date"
        value={termin}
        onChange={(e) => setTermin(e.target.value)}
        aria-label="Termin"
        title="Darf leer bleiben – dann steht der Meilenstein ohne Datum"
        className="rounded-md border border-line bg-surface px-2 py-1 text-[12px]"
      />
      <Button type="submit" variant="ghost" size="sm" disabled={laeuft || !gueltig}>
        {aktion}
      </Button>
      <Button type="button" variant="ghost" size="sm" onClick={onAbbrechen}>
        ✕
      </Button>
      {onEntfernen && (
        <button
          type="button"
          onClick={onEntfernen}
          disabled={laeuft}
          className="cursor-pointer text-[11.5px] text-muted hover:text-pool-sofort hover:underline"
        >
          löschen
        </button>
      )}
    </form>
  )
}

/**
 * **Wie ein Termin in einer Liste steht** – und was dort steht, wenn keiner gesetzt ist.
 *
 * „Termin offen" statt einer leeren Stelle: leer läse sich wie ein Darstellungsfehler, und der
 * Unterschied zwischen *„noch nicht terminiert"* und *„da ist etwas kaputt"* ist genau der,
 * um den es hier geht.
 */
export function terminText(termin: string | null): string {
  return termin === null ? 'Termin offen' : new Date(termin).toLocaleDateString('de-DE')
}
